(() => {
  const ROOT_SELECTOR = 'db-tabbed-collections';
  const CARD_SELECTOR = '[data-dbtc-config-card]';
  const CHOICE_SELECTOR = '[data-dbtccfg-choice]';
  const SELECT_SELECTOR = '[data-dbtccfg-select]';
  const productCache = new WeakMap();

  const readProduct = (card) => {
    if (productCache.has(card)) return productCache.get(card);

    const source = card.querySelector('[data-dbtccfg-product]');
    if (!source) return null;

    try {
      const product = JSON.parse(source.textContent);
      productCache.set(card, product);
      return product;
    } catch (error) {
      console.warn('[DB product card configurator]', error);
      return null;
    }
  };

  const controlledIndexes = (card) => {
    return Array.from(card.querySelectorAll('[data-dbtccfg-option-index]'), (group) => Number(group.dataset.dbtccfgOptionIndex))
      .filter(Number.isInteger);
  };

  const selectedOptions = (card, product) => {
    const selectedVariantId = Number(card.dataset.dbtcSelectedVariant);
    const selectedVariant = product.variants.find((variant) => Number(variant.id) === selectedVariantId)
      || product.variants.find((variant) => variant.available)
      || product.variants[0];

    const values = selectedVariant ? [...selectedVariant.options] : Array(product.options.length).fill(null);

    card.querySelectorAll('[data-dbtccfg-option-index]').forEach((group) => {
      const index = Number(group.dataset.dbtccfgOptionIndex);
      const select = group.querySelector(SELECT_SELECTOR);
      const selected = group.querySelector(`${CHOICE_SELECTOR}[aria-pressed="true"]`);
      if (Number.isInteger(index) && select) values[index] = select.value;
      else if (Number.isInteger(index) && selected) values[index] = selected.dataset.optionValue;
    });

    return values;
  };

  const findVariant = (product, values, indexes, preferredIndex) => {
    const exact = product.variants.find((variant) => {
      return values.every((value, index) => value == null || variant.options[index] === value);
    });

    if (exact?.available) return exact;

    const preferredValue = values[preferredIndex];
    const matchingControlled = product.variants.find((variant) => {
      if (!variant.available || variant.options[preferredIndex] !== preferredValue) return false;
      return indexes.every((index) => index === preferredIndex || variant.options[index] === values[index]);
    });

    return matchingControlled
      || product.variants.find((variant) => variant.available && variant.options[preferredIndex] === preferredValue)
      || exact
      || product.variants.find((variant) => variant.available)
      || product.variants[0];
  };

  const formatMoney = (cents, currency) => {
    const locale = document.documentElement.lang || 'en';
    const activeCurrency = currency || window.Shopify?.currency?.active || window.Shopify?.currency?.current || 'USD';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency
      }).format(Number(cents) / 100);
    } catch (_) {
      return `${(Number(cents) / 100).toFixed(2)} ${activeCurrency}`;
    }
  };

  const variantUrl = (baseUrl, variantId) => {
    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return baseUrl;
    }
  };

  const renderPrice = (card, product, variant) => {
    const price = card.querySelector('[data-dbtccfg-price]');
    if (!price) return;

    const current = Number(variant.price || 0);
    const compareAt = Number(variant.compareAtPrice || 0);
    const onSale = compareAt > current;

    price.replaceChildren();

    const currentPrice = document.createElement('span');
    currentPrice.className = `dbtc__price-current${onSale ? ' dbtc__price-current--sale' : ''}`;
    currentPrice.textContent = formatMoney(current, product.currency);
    price.append(currentPrice);

    if (onSale) {
      const comparePrice = document.createElement('span');
      comparePrice.className = 'dbtc__compare-price';
      comparePrice.textContent = formatMoney(compareAt, product.currency);
      price.append(comparePrice);
    }
  };

  const renderChoices = (card, product, variant) => {
    const indexes = controlledIndexes(card);

    card.querySelectorAll('[data-dbtccfg-option-index]').forEach((group) => {
      const index = Number(group.dataset.dbtccfgOptionIndex);
      const value = variant.options[index];

      group.querySelectorAll(CHOICE_SELECTOR).forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice.dataset.optionValue === value));
      });

      const select = group.querySelector(SELECT_SELECTOR);
      if (select) {
        select.value = value;
        Array.from(select.options).forEach((option) => {
          const candidate = [...variant.options];
          candidate[index] = option.value;

          option.disabled = !product.variants.some((candidateVariant) => {
            if (!candidateVariant.available) return false;
            return indexes.every((controlledIndex) => candidateVariant.options[controlledIndex] === candidate[controlledIndex]);
          });
        });
      }

      const selectedValue = group.querySelector('[data-dbtccfg-selected-value]');
      if (selectedValue) selectedValue.textContent = value || '';
    });

    card.querySelectorAll(CHOICE_SELECTOR).forEach((choice) => {
      const index = Number(choice.dataset.optionIndex);
      const candidate = [...variant.options];
      candidate[index] = choice.dataset.optionValue;

      const isAvailable = product.variants.some((candidateVariant) => {
        if (!candidateVariant.available) return false;
        return indexes.every((controlledIndex) => candidateVariant.options[controlledIndex] === candidate[controlledIndex]);
      });

      choice.disabled = !isAvailable;
    });
  };

  const renderVariant = (card, product, variant) => {
    if (!variant) return;

    card.dataset.dbtcSelectedVariant = variant.id;
    renderChoices(card, product, variant);
    renderPrice(card, product, variant);

    const input = card.querySelector('[data-dbtccfg-variant-input]');
    if (input) input.value = variant.id;

    const addButton = card.querySelector('[data-dbtccfg-add]');
    const addLabel = card.querySelector('[data-dbtccfg-add-label]');

    if (addButton) addButton.disabled = !variant.available;
    if (addLabel) addLabel.textContent = variant.available ? product.labels.add : product.labels.soldOut;

    const destination = variantUrl(product.url, variant.id);
    card.querySelectorAll('.dbtc__media-link, [data-dbtccfg-product-link]').forEach((link) => {
      link.href = destination;
    });

    const image = card.querySelector('.dbtc__image');
    if (image && variant.image) {
      const source = new URL(variant.image, document.baseURI);
      const current = new URL(image.src, document.baseURI);
      if (current.pathname !== source.pathname) {
        image.srcset = [240, 320, 400, 480, 600, 800, 1000, 1200].map((width) => {
          const candidate = new URL(source);
          candidate.searchParams.set('width', width);
          return `${candidate.href} ${width}w`;
        }).join(', ');
        image.src = source.href;
      }
    }

    card.dispatchEvent(new CustomEvent('db:tabbed-card-variant:change', {
      bubbles: true,
      detail: { product, variant }
    }));
  };

  document.addEventListener('click', (event) => {
    const choice = event.target.closest(CHOICE_SELECTOR);
    if (!choice || choice.disabled) return;

    const card = choice.closest(CARD_SELECTOR);
    if (!card || !card.closest(ROOT_SELECTOR)) return;

    const product = readProduct(card);
    if (!product?.variants?.length) return;

    const index = Number(choice.dataset.optionIndex);
    if (!Number.isInteger(index)) return;

    const values = selectedOptions(card, product);
    values[index] = choice.dataset.optionValue;

    const variant = findVariant(product, values, controlledIndexes(card), index);
    renderVariant(card, product, variant);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest(SELECT_SELECTOR);
    if (!select) return;

    const card = select.closest(CARD_SELECTOR);
    if (!card || !card.closest(ROOT_SELECTOR)) return;

    const product = readProduct(card);
    if (!product?.variants?.length) return;

    const index = Number(select.dataset.optionIndex);
    if (!Number.isInteger(index)) return;

    const values = selectedOptions(card, product);
    values[index] = select.value;

    const variant = findVariant(product, values, controlledIndexes(card), index);
    renderVariant(card, product, variant);
  });
})();

